<script setup lang="ts">
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import PlayerVideo from '~/entities/player-video/ui/PlayerVideo.vue';
import ContentNews from './ContentNews.vue';
import ContentCompanies from './ContentCompanies.vue';
import {
  CLIENT_COMPANIES_FEED_KEY,
  CLIENT_COMPANIES_FEED_URL,
  CLIENT_FEED_SPECIAL_KEY,
  CLIENT_FEED_SPECIAL_PREVIEW_KEY,
  CLIENT_FEED_SPECIAL_PREVIEW_URL,
  CLIENT_FEED_SPECIAL_URL,
  CLIENT_NEWS_FEED_KEY,
  CLIENT_NEWS_FEED_URL,
} from '~/shared/api/client/publisher/publisher.const';
import type {
  IFeedDataClientDTO,
  IFeedItemVideoDataClientDTO,
} from '~/shared/api/client/feed/feed.dto';
import type { ISpecialProjectFeedSettingsClientDTO } from '~/shared/api/client/special-project-feed/special-project-feed.dto';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';

const isDesktop = computed(() =>
  useNuxtApp().$viewport.isGreaterOrEquals('tabletBig'),
);

const isArticle = computed(() => useRoute().meta.layoutType === 'article');

// Partner article: the side feed (with its banners) is hidden entirely.
// The flag is set by the partner-article.global middleware before render,
// so it is already correct during SSR.
const isPartnerView = useState<boolean>('is-partner-article', () => false);

const activeTab = ref(isDesktop.value ? 'news' : 'main');
type TContentTab = 'main' | 'news' | 'companies';

const setContentTab = (tab: TContentTab) => {
  if (tab === 'main' && isDesktop.value) {
    return;
  }

  activeTab.value = tab;
};

provide('setContentTab', setContentTab);

const videoItem = computed(() =>
  activeTab.value === 'companies'
    ? (feedCompanyData.value?.[0]
        ?.publisher_news[0] as IFeedItemVideoDataClientDTO)
    : (feedData.value?.[0]?.publisher_news[0] as IFeedItemVideoDataClientDTO),
);

const { data: feedData, status: feedStatus } = useCustomFetch<
  IFeedDataClientDTO[]
>(CLIENT_NEWS_FEED_URL, {
  method: 'GET',
  key: CLIENT_NEWS_FEED_KEY,
});

const { data: feedCompanyData, status: feedCompanyStatus } = useCustomFetch<
  IFeedDataClientDTO[]
>(CLIENT_COMPANIES_FEED_URL, {
  method: 'GET',
  key: CLIENT_COMPANIES_FEED_KEY,
});

// «Спецпроект Ленты» is a standalone publisher; preview uses ?special_feed_hash
// (?hash is already taken by the chronicle grid preview)
const route = useRoute();
const isSpecialFeedPreview = computed(() =>
  Boolean(route.query?.special_feed_hash),
);

const { data: specialFeedData } = useCustomFetch<
  ISpecialProjectFeedSettingsClientDTO[]
>(
  isSpecialFeedPreview.value
    ? CLIENT_FEED_SPECIAL_PREVIEW_URL
    : CLIENT_FEED_SPECIAL_URL,
  {
    method: 'GET',
    key: isSpecialFeedPreview.value
      ? CLIENT_FEED_SPECIAL_PREVIEW_KEY
      : CLIENT_FEED_SPECIAL_KEY,
    params: isSpecialFeedPreview.value
      ? { hash: route.query?.special_feed_hash }
      : undefined,
  },
);

// Бэк отдаёт каждую новость отдельной строкой (сортировка по y), плюс строка
// настроек. Склеиваем publisher_news всех строк в одну, чтобы SpecialProjectFeed
// рендерил настройки и новости из одного списка.
const specialFeed = computed<ISpecialProjectFeedSettingsClientDTO | undefined>(
  () => {
    const rows = specialFeedData.value;
    const firstRow = rows?.[0];
    if (!firstRow) {
      return undefined;
    }

    return {
      ...firstRow,
      publisher_news: rows.flatMap((row) => row.publisher_news ?? []),
    };
  },
);

watch(
  () => isDesktop.value,
  (newValue) => {
    if (newValue && activeTab.value === 'main') {
      activeTab.value = 'news';
    }

    if (!newValue && activeTab.value === 'news') {
      activeTab.value = 'main';
    }
  },
);

const isNeedChangeTab = inject<Ref<boolean>>('isNeedChangeTab', ref(false));

watch(
  () => isNeedChangeTab.value,
  (newValue) => {
    if (newValue === true && activeTab.value !== 'main' && !isDesktop.value) {
      activeTab.value = 'main';
    }
  },
);

watch(
  () => activeTab.value,
  () => {
    isNeedChangeTab.value = false;
  },
);
</script>

<template>
  <div :class="$style.root">
    <div v-if="!isPartnerView" :class="$style.tabsWrapper">
      <TabsRoot v-model="activeTab" :class="$style.tabs">
        <PlayerVideo
          v-if="
            activeTab !== 'main' &&
            videoItem?.title &&
            videoItem?.subject && // в subject содержимое iframe
            videoItem?.image_one?.file_url &&
            videoItem?.image_two?.file_url
          "
          v-bind="videoItem"
          :class="[$style.player, { [$style.player_article!]: isArticle }]"
        />
        <TelegramMain
          v-if="activeTab !== 'main' && !isDesktop"
          :class="$style.telegram"
        />
        <TabsList
          aria-label="Главные новости и Новости компаний"
          :class="$style.categorySelector"
        >
          <TabsTrigger
            v-if="!isDesktop"
            as="div"
            :class="$style.categorySelector__item"
            value="main"
          >
            Главное
          </TabsTrigger>
          <TabsTrigger
            as="div"
            :class="$style.categorySelector__item"
            value="news"
          >
            Новости
          </TabsTrigger>
          <TabsTrigger
            as="div"
            :class="$style.categorySelector__item"
            value="companies"
          >
            Компании
          </TabsTrigger>
        </TabsList>
        <TabsContent value="news" :class="$style.aside">
          <ContentNews
            :data="feedData?.slice(1)"
            :status="feedStatus"
            :special-feed="specialFeed"
          />
        </TabsContent>
        <TabsContent value="companies" :class="$style.aside">
          <ContentCompanies
            :data="feedCompanyData?.slice(1)"
            :status="feedCompanyStatus"
          />
        </TabsContent>
        <TabsContent
          v-if="!isDesktop"
          value="main"
          :class="[$style.aside, $style.aside_main]"
        >
          <TelegramMain />
          <slot />
        </TabsContent>
      </TabsRoot>
    </div>
    <div v-if="isDesktop || isPartnerView" :class="$style.main">
      <slot />
    </div>
  </div>
</template>
<style lang="scss" module>
.root {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.aside {
  width: 300px;
  flex-shrink: 0;
}

.main {
  position: sticky;
  bottom: 0;
  align-self: flex-end;
  min-width: 0;
  width: 100%;
  overflow: hidden;
}

.tabs {
  display: flex;
  flex-flow: column nowrap;
}

.tabsWrapper {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  bottom: 0;
  align-self: flex-end;
  position: sticky;
  z-index: 1;
}

.categorySelector {
  display: flex;
  flex-flow: row nowrap;
  height: 40px;
  border-bottom: 1px solid var(--color-gray-line);
  flex-shrink: 0;

  &__item {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    @include text-body2_medium;
    color: var(--color-gray-text);
    background-color: var(--color-gray-bg);
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      color: var(--color-red-1);
    }

    &[data-state='active'] {
      color: var(--color-black-1);
      background-color: var(--color-white);
      &:hover {
        color: var(--color-red-1);
      }
    }
  }
}

.player {
  margin-top: var(--space-2-5x);
  position: relative;
  border-top: 1px solid var(--color-gray-line);

  &::before {
    position: absolute;
    content: '';
    top: -21px;
    right: -1px;
    width: 1px;
    height: var(--space-2-5x);
    background-color: var(--color-white);
  }

  &_article {
    &::before {
      left: -1px;
      right: initial;
    }
  }
}

.telegram {
  order: 2;
}

@media (max-width: 1379px) {
  .aside {
    overflow: hidden;
  }
}

@media (min-width: 1024px) {
  .root {
    flex-direction: row;
  }
  .tabs {
    &Wrapper {
      display: flex;
      flex-flow: column nowrap;
      width: 300px;
      flex-shrink: 0;
    }
  }
}

@media (max-width: 1023px) {
  .tabs {
    width: 100%;
  }

  .player {
    order: 3;
    margin-top: var(--space-2x);
    border-top: none;
    &::before {
      display: none;
      position: static;
    }
  }

  .categorySelector {
    order: 1;
    &__item {
      &:nth-child(2) {
        border-left: 1px solid var(--color-gray-line);
        border-right: 1px solid var(--color-gray-line);
      }

      &:not(:first-child) {
        flex-grow: 1;
      }
    }
  }

  .aside {
    width: 100%;
    min-width: 0;
    flex-shrink: auto;
    order: 4;
    margin-top: var(--space-2x);

    &_main {
      margin-top: 0;
    }
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .categorySelector {
    &__item {
      &:first-child {
        width: 300px;
        flex-shrink: 0;
      }
    }
  }
}
</style>
